import React, { memo } from "react";
import { useWindowDimensions } from "react-native";
import { Box } from "app/components/common/Box";
import { getCurrentRoute, getTabNotifications } from "./utils";
import { useDispatch, useSelector } from "react-redux";
import { getWebDesktopTabs } from "@microdoing/shared/services/profileSettings/selectors";
import { TabBarIconDesktop } from "./IconTabBar";
import theme from "app/Theme/Theme";
import LogoMiniDark from "app/components/Icons/logo/LogoMiniDark";
import { MainText, PressableOpacity } from "app/components/common";
import i18n from "@microdoing/shared/i18n";
import LogoFullMicrodoing from "app/components/Icons/logo/LogoFullMicrodoing";
import {
  getNewGeneralNotifications,
  getNewHomeNotifications,
  getNewMessageNotifications,
  getNewResourceNotifications,
} from "@microdoing/shared/services/notification/selectors";
import normalize from "app/utils/normalize";
import {
  clearGeneralNotifications,
  clearHomeNotifications,
  clearMessageNotifications,
  getNotificationsRequest,
} from "@microdoing/shared/services/notification/actions";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import { getRoute } from "@microdoing/shared/services/nav/selectors";

interface ITab {
  index: number;
  key: string;
  icon: string;
  route: string;
  tabBarTestID: string;
  label: string;
}

interface ITabBar {
  navigation?: any;
  isForbidden: boolean;
  state: any;
  achievement: { name: string; level: number };
  language: string;
  userPicture: string;
  firstName: string;
  tabs: ITab[];
  isValidateRequesting: boolean;
  userId: string;
}

interface IPropsTab {
  activeTab: string;
  tab: ITab;
  onPress: () => void;
  newMessageNotifications: number;
  newHomeNotifications: number;
  newGeneralNotifications: number;
  newResourceNotifications: number;
}

const Tab = memo(
  ({
    activeTab,
    tab,
    onPress,
    newMessageNotifications,
    newHomeNotifications,
    newGeneralNotifications,
    newResourceNotifications,
  }: IPropsTab) => {
    const isActive = activeTab === tab.route;
    const { width } = useWindowDimensions();

    const isWebBigScreen = width >= theme.breakpoints.webMedium;
    const notifications = getTabNotifications({
      tabName: tab.key,
      isActive,
      newMessageNotifications,
      newHomeNotifications,
      newGeneralNotifications,
      newResourceNotifications,
    });

    return (
      <PressableOpacity
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: theme.spacing.s,
        }}
        onPress={onPress}
      >
        <>
          <Box
            borderRadius={6}
            paddingHorizontal="xs"
            paddingVertical="xs"
            style={{
              cursor: "pointer",
              boxShadow: isActive ? "0px 4px 10px rgba(0, 0, 0, 0.05)" : "none",
            }}
          >
            {TabBarIconDesktop(
              tab.icon,
              isActive
                ? theme.colors["brand.500"]
                : theme.colors["neutral.400"],
            )}
          </Box>
          {!!notifications && (
            <Box
              position="absolute"
              minWidth={normalize(20)}
              backgroundColor="system.red.900"
              borderRadius={50}
              justifyContent="center"
              alignItems="center"
              style={{
                top: 5,
                left: 25,
              }}
            >
              <MainText
                variant="l3-semibold"
                color="neutral.white"
                paddingHorizontal="xs"
              >
                {notifications}
              </MainText>
            </Box>
          )}
        </>
        {isWebBigScreen && (
          <MainText
            variant={isActive ? "l4-semibold" : "l4-medium"}
            color={isActive ? "brand.500" : "neutral.400"}
            marginTop="xxs"
            marginLeft="xm"
          >
            {tab.label}
          </MainText>
        )}
      </PressableOpacity>
    );
  },
);

const settingTab = {
  key: "Settings",
  route: "UserProfileSettings",
  icon: "settings",
  index: 5,
  tabBarTestID: "tab-settings",
  label: i18n.t("breadcrumb-profile-settings"),
};

const DesktopTabBar = ({ navigation, state }: ITabBar) => {
  const newMessageNotifications = useSelector(getNewMessageNotifications);
  const newHomeNotifications = useSelector(getNewHomeNotifications);
  const newGeneralNotifications = useSelector(getNewGeneralNotifications);
  const newResourceNotifications = useSelector(getNewResourceNotifications);
  const dispatch = useDispatch();
  const tabs = useSelector(getWebDesktopTabs);
  const { width } = useWindowDimensions();
  const activeRouteName = useSelector(getRoute);
  const isWebBigScreen = width >= theme.breakpoints.webMedium;
  const userId = useSelector(getUserId);
  const activeTab = getCurrentRoute(state);
  const onPressTab = route => () => {
    if (
      (route === "Message" && newMessageNotifications) ||
      (activeRouteName === "Message" && newMessageNotifications)
    ) {
      dispatch(clearMessageNotifications());
    }

    if (route === "Notifications" && newGeneralNotifications) {
      dispatch(
        getNotificationsRequest({
          page: 0,
          userId: userId,
        }),
      );
      dispatch(clearGeneralNotifications());
    }
    if (activeRouteName === "Notifications" && newGeneralNotifications) {
      dispatch(clearGeneralNotifications());
    }
    if (
      (route === "DailyFocusScreen" && newHomeNotifications) ||
      (activeRouteName === "DailyFocusScreen" && newHomeNotifications)
    ) {
      dispatch(clearHomeNotifications());
    }
    navigation.navigate(route);
  };

  return (
    <Box variant="webTabBar">
      <Box
        alignItems="flex-start"
        flex={1}
        minWidth={{ smallWeb: "auto", webMedium: 180 }}
        marginRight={{ smallWeb: "m", webMedium: "webM" }}
        marginLeft={{ smallWeb: "m", webMedium: "xl" }}
        maxHeight={1000}
      >
        <Box flex={1}>
          <Box marginBottom="xxl" marginLeft="xs">
            {isWebBigScreen ? (
              <LogoFullMicrodoing height={25} width={115} />
            ) : (
              <LogoMiniDark />
            )}
          </Box>
          <Box>
            {tabs.map(tab => (
              <Tab
                key={tab.index}
                activeTab={activeTab}
                tab={tab}
                onPress={onPressTab(tab?.route)}
                newMessageNotifications={newMessageNotifications}
                newHomeNotifications={newHomeNotifications}
                newGeneralNotifications={newGeneralNotifications}
                newResourceNotifications={newResourceNotifications}
              />
            ))}
          </Box>
        </Box>
        <Tab
          activeTab={activeTab}
          tab={settingTab}
          onPress={onPressTab(settingTab.route)}
          newMessageNotifications={newMessageNotifications}
          newHomeNotifications={newHomeNotifications}
          newGeneralNotifications={newGeneralNotifications}
          newResourceNotifications={newResourceNotifications}
        />
      </Box>
    </Box>
  );
};

export default DesktopTabBar;
